import { useCallback } from "react";

import { KindergartenRoleEnum } from "@king-ict/eupisi-ui/models";

import { useTypedSelector } from "../../app/rootReducer";
import EnrollmentTypeEnum from "../../models/EnrollmentType/EnrollmentTypeEnum";
import ImportantDateEventEnum from "../../models/ImportantDate/ImportantDateEventEnum";
import RequestBasicInfo from "../../models/Request/RequestBasicInfo";
import RequestStatusEnum from "../../models/Request/RequestStatusEnum";
import RequestTypeEnum from "../../models/Request/RequestTypeEnum";

export const useBusinessRule = () => {
  const pedagogicalYear = useTypedSelector((state) => state.pedagogicalYear);

  const user = useTypedSelector((state) => state.application.user);

  const isLoginEnabled = useCallback(() => {
    return (
      pedagogicalYear.currentImportantDate.eventId &&
      pedagogicalYear.currentImportantDate.eventId !==
      ImportantDateEventEnum.InstitutionAdministration
    );
  }, [pedagogicalYear.currentImportantDate.eventId]);

  const isInstitutionAdministrationActive = () => {
    return (
      pedagogicalYear.currentImportantDate.eventId ===
      ImportantDateEventEnum.InstitutionAdministration
    );
  };

  const isRequestCreationAllowed = useCallback(() => {
    return (
      pedagogicalYear.currentPedagogicalYear.id &&
      pedagogicalYear.currentImportantDate.eventId ===
      ImportantDateEventEnum.RequestCreation
    );
  }, [pedagogicalYear]);

  const isMaximumRequestCountReached = (
    childRequestsCount: number,
    requestType: RequestTypeEnum
  ) => {
    return pedagogicalYear.currentImportantDate.enrollmentTypeId ===
      EnrollmentTypeEnum.SubsequentEnrollment ||
      requestType === RequestTypeEnum.Preschool
      ? childRequestsCount >= 1
      : childRequestsCount >= 2;
  };

  const isPreschoolRequestAllowed = (childBirthDate: Date) => {
    const lastDateInYear = new Date(
      new Date(Date.now()).getFullYear(),
      11,
      31,
      23,
      59,
      59,
      59
    );
    let childDate = new Date(childBirthDate);
    let age = lastDateInYear.getFullYear() - childDate.getFullYear();
    childDate.setFullYear(childDate.getFullYear() + age);
    if (lastDateInYear < childDate) {
      age--;
    }
    return age >= 5;
  };

  const isRequestDeletionAllowed = (requestBasicInfo: RequestBasicInfo) => {
    return (
      pedagogicalYear.currentImportantDate.enrollmentTypeId ===
      requestBasicInfo.enrollmentTypeId &&
      pedagogicalYear.currentImportantDate.monthId ===
      requestBasicInfo.monthId &&
      user.userId === requestBasicInfo.createdByAppUserID &&
      (requestBasicInfo.applicantParentOib === user.oib ||
        ((KindergartenRoleEnum.KindergartenAdmin === user.roleId ||
          KindergartenRoleEnum.KindergartenDirector === user.roleId) &&
          requestBasicInfo.createdByAdmin)) &&
      requestBasicInfo.pedagogicalYearId ===
      pedagogicalYear.currentPedagogicalYear.id &&
      pedagogicalYear.currentImportantDate.eventId ===
      ImportantDateEventEnum.RequestCreation &&
      (requestBasicInfo.statusId === RequestStatusEnum.Created ||
        requestBasicInfo.statusId === RequestStatusEnum.Draft)
    );
  };

  const isRequestEditingAllowed = useCallback(
    (requestBasicInfo: RequestBasicInfo) => {
      return (
        pedagogicalYear.currentImportantDate.enrollmentTypeId ===
        requestBasicInfo.enrollmentTypeId &&
        pedagogicalYear.currentImportantDate.monthId ===
        requestBasicInfo.monthId &&
        user.userId === requestBasicInfo.createdByAppUserID &&
        (requestBasicInfo.applicantParentOib === user.oib ||
          ((KindergartenRoleEnum.KindergartenAdmin === user.roleId ||
            KindergartenRoleEnum.KindergartenDirector === user.roleId) &&
            requestBasicInfo.createdByAdmin)) &&
        requestBasicInfo.pedagogicalYearId ===
        pedagogicalYear.currentPedagogicalYear.id &&
        ((pedagogicalYear.currentImportantDate.eventId ===
          ImportantDateEventEnum.RequestCreation &&
          (requestBasicInfo.statusId === RequestStatusEnum.Created ||
            requestBasicInfo.statusId === RequestStatusEnum.Draft)) ||
          (pedagogicalYear.currentImportantDate.eventId ===
            ImportantDateEventEnum.RequestEvaluation &&
            requestBasicInfo.statusId === RequestStatusEnum.Unlocked))
      );
    },
    [
      pedagogicalYear.currentImportantDate.monthId,
      pedagogicalYear.currentImportantDate.enrollmentTypeId,
      pedagogicalYear.currentImportantDate.eventId,
      pedagogicalYear.currentPedagogicalYear.id,
      user.userId,
      user.oib,
      user.roleId,
    ]
  );

  const isPhysicalExaminationAllowed = (applicantParentOib: string) => {
    return applicantParentOib === user.oib;
  };

  const isApplicantParent = (applicantParentOib: string) => {
    return applicantParentOib === user.oib;
  };

  return {
    isLoginEnabled,
    isInstitutionAdministrationActive,
    isRequestCreationAllowed,
    isPreschoolRequestAllowed,
    isMaximumRequestCountReached,
    isRequestDeletionAllowed,
    isRequestEditingAllowed,
    isPhysicalExaminationAllowed,
    isApplicantParent,
  };
};
